import React, { useState, useRef, useCallback, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Camera, Upload, Image, Sparkles, Download, Share2, Instagram,
  Sliders, Sun, Contrast, Palette, Wand2, RotateCcw, ZoomIn, ZoomOut,
  Move, Crop, Type, Layers, Save, Eye, Grid3X3, Heart, MessageCircle,
  Bookmark, Send, MoreHorizontal, Play, Smartphone, Monitor, Square,
  Circle, X, Check, ChevronLeft, ChevronRight, User, Shirt, Crown,
  Filter, Droplets, Thermometer, Focus, Maximize2, FlipHorizontal,
  FlipVertical, Undo, Redo, PaintBucket, Eraser, Brush, Star
} from 'lucide-react';

// Instagram post templates
const TEMPLATES = [
  {
    id: 1,
    name: 'Clean Minimal',
    category: 'minimal',
    layout: 'center',
    background: 'linear-gradient(135deg, #fdfbfb 0%, #ebedee 100%)',
    style: { filter: 'none', borderRadius: '0' },
    preview: '🤍'
  },
  {
    id: 2,
    name: 'Warm Sunset',
    category: 'warm',
    layout: 'center',
    background: 'linear-gradient(135deg, #f5af19 0%, #f12711 100%)',
    style: { filter: 'sepia(20%) saturate(1.2)' },
    preview: '🌅'
  },
  {
    id: 3,
    name: 'Cool Ocean',
    category: 'cool',
    layout: 'center',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    style: { filter: 'hue-rotate(10deg) saturate(1.1)' },
    preview: '🌊'
  },
  {
    id: 4,
    name: 'Soft Pink',
    category: 'soft',
    layout: 'center',
    background: 'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)',
    style: { filter: 'saturate(0.9) brightness(1.05)' },
    preview: '🌸'
  },
  {
    id: 5,
    name: 'Dark Luxury',
    category: 'dark',
    layout: 'center',
    background: 'linear-gradient(135deg, #232526 0%, #414345 100%)',
    style: { filter: 'contrast(1.1) brightness(0.95)' },
    preview: '🖤'
  },
  {
    id: 6,
    name: 'Vintage Film',
    category: 'vintage',
    layout: 'center',
    background: 'linear-gradient(135deg, #c9d6ff 0%, #e2e2e2 100%)',
    style: { filter: 'sepia(30%) contrast(0.9) brightness(1.1)' },
    preview: '📷'
  },
  {
    id: 7,
    name: 'Neon Glow',
    category: 'bold',
    layout: 'center',
    background: 'linear-gradient(135deg, #ff0844 0%, #ffb199 100%)',
    style: { filter: 'saturate(1.3) contrast(1.05)' },
    preview: '✨'
  },
  {
    id: 8,
    name: 'Natural Earth',
    category: 'natural',
    layout: 'center',
    background: 'linear-gradient(135deg, #c1dfc4 0%, #deecdd 100%)',
    style: { filter: 'saturate(0.85) sepia(5%)' },
    preview: '🌿'
  },
  {
    id: 9,
    name: 'Golden Hour',
    category: 'warm',
    layout: 'center',
    background: 'linear-gradient(135deg, #FFD194 0%, #D1913C 100%)',
    style: { filter: 'sepia(15%) saturate(1.3) brightness(1.1)' },
    preview: '🌤️'
  },
  {
    id: 10,
    name: 'Moody Blue',
    category: 'dark',
    layout: 'center',
    background: 'linear-gradient(135deg, #0f0c29 0%, #302b63 50%, #24243e 100%)',
    style: { filter: 'hue-rotate(-10deg) saturate(0.8) brightness(0.9)' },
    preview: '🌙'
  },
  {
    id: 11,
    name: 'Pastel Dream',
    category: 'soft',
    layout: 'center',
    background: 'linear-gradient(135deg, #a18cd1 0%, #fbc2eb 100%)',
    style: { filter: 'brightness(1.1) saturate(0.9)' },
    preview: '💜'
  },
  {
    id: 12,
    name: 'Retro 70s',
    category: 'vintage',
    layout: 'center',
    background: 'linear-gradient(135deg, #f4d03f 0%, #16a085 100%)',
    style: { filter: 'sepia(25%) hue-rotate(-15deg) saturate(1.2)' },
    preview: '🕺'
  },
  {
    id: 13,
    name: 'Ice Queen',
    category: 'cool',
    layout: 'center',
    background: 'linear-gradient(135deg, #E0EAFC 0%, #CFDEF3 100%)',
    style: { filter: 'hue-rotate(20deg) brightness(1.1) saturate(0.8)' },
    preview: '❄️'
  },
  {
    id: 14,
    name: 'Forest Vibes',
    category: 'natural',
    layout: 'center',
    background: 'linear-gradient(135deg, #134E5E 0%, #71B280 100%)',
    style: { filter: 'hue-rotate(30deg) saturate(0.9)' },
    preview: '🌲'
  },
  {
    id: 15,
    name: 'Coral Reef',
    category: 'bold',
    layout: 'center',
    background: 'linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%)',
    style: { filter: 'saturate(1.2) brightness(1.05)' },
    preview: '🪸'
  },
  {
    id: 16,
    name: 'Noir Classic',
    category: 'dark',
    layout: 'center',
    background: 'linear-gradient(135deg, #000000 0%, #434343 100%)',
    style: { filter: 'grayscale(0.3) contrast(1.2)' },
    preview: '🎬'
  }
];

// Photo filters - Expanded with 24 filters
const FILTERS = [
  { id: 'none', name: 'Original', filter: 'none' },
  { id: 'clarendon', name: 'Clarendon', filter: 'contrast(1.2) saturate(1.35)' },
  { id: 'gingham', name: 'Gingham', filter: 'brightness(1.05) sepia(0.1) hue-rotate(-10deg)' },
  { id: 'moon', name: 'Moon', filter: 'grayscale(1) contrast(1.1) brightness(1.1)' },
  { id: 'lark', name: 'Lark', filter: 'contrast(0.9) saturate(1.15) brightness(1.1)' },
  { id: 'reyes', name: 'Reyes', filter: 'sepia(0.22) brightness(1.1) contrast(0.85) saturate(0.75)' },
  { id: 'juno', name: 'Juno', filter: 'sepia(0.1) saturate(1.5) contrast(1.1)' },
  { id: 'slumber', name: 'Slumber', filter: 'saturate(0.66) brightness(1.05) sepia(0.1)' },
  { id: 'crema', name: 'Crema', filter: 'sepia(0.2) saturate(0.9) brightness(1.05)' },
  { id: 'ludwig', name: 'Ludwig', filter: 'saturate(1.2) contrast(0.95) sepia(0.05)' },
  { id: 'aden', name: 'Aden', filter: 'hue-rotate(-20deg) contrast(0.9) saturate(0.85) brightness(1.2)' },
  { id: 'perpetua', name: 'Perpetua', filter: 'brightness(1.1) saturate(1.1)' },
  { id: 'valencia', name: 'Valencia', filter: 'sepia(0.15) saturate(1.5) contrast(0.9) brightness(1.1)' },
  { id: 'xpro2', name: 'X-Pro II', filter: 'sepia(0.3) contrast(1.3) saturate(1.3)' },
  { id: 'lofi', name: 'Lo-Fi', filter: 'saturate(1.4) contrast(1.3)' },
  { id: 'inkwell', name: 'Inkwell', filter: 'grayscale(1) brightness(1.1)' },
  { id: 'nashville', name: 'Nashville', filter: 'sepia(0.4) saturate(1.5) brightness(1.1) hue-rotate(-15deg)' },
  { id: 'stinson', name: 'Stinson', filter: 'brightness(1.2) saturate(0.8)' },
  { id: 'vesper', name: 'Vesper', filter: 'sepia(0.35) contrast(1.15) brightness(1.2) saturate(1.3)' },
  { id: 'earlybird', name: 'Earlybird', filter: 'sepia(0.25) contrast(1.1) brightness(1.05) saturate(0.9)' },
  { id: 'brannan', name: 'Brannan', filter: 'sepia(0.5) contrast(1.4)' },
  { id: 'hefe', name: 'Hefe', filter: 'contrast(1.3) saturate(1.2) sepia(0.1)' },
  { id: 'hudson', name: 'Hudson', filter: 'brightness(1.2) contrast(0.9) saturate(0.85)' },
  { id: 'rise', name: 'Rise', filter: 'brightness(1.1) sepia(0.15) saturate(1.2) contrast(0.85)' },
];

const ContentStudio = () => {
  // Image states
  const [userPhoto, setUserPhoto] = useState(null);
  const [processedImage, setProcessedImage] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(TEMPLATES[0]);
  const [selectedFilter, setSelectedFilter] = useState(FILTERS[0]);
  
  // Editing states
  const [adjustments, setAdjustments] = useState({
    brightness: 100,
    contrast: 100,
    saturation: 100,
    warmth: 0,
    exposure: 50,
    highlights: 50,
    shadows: 50,
    sharpness: 0,
    vignette: 0,
    blur: 0,
    fade: 0,
    grain: 0,
    tint: 50,
    vibrance: 50
  });
  
  // Transform states
  const [transforms, setTransforms] = useState({
    scale: 1,
    rotation: 0,
    flipH: false,
    flipV: false,
    x: 0,
    y: 0
  });
  
  // UI states
  const [activeTab, setActiveTab] = useState('templates');
  const [aspectRatio, setAspectRatio] = useState('1:1');
  const [showPreview, setShowPreview] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [caption, setCaption] = useState('');
  const [history, setHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  
  const fileInputRef = useRef(null);
  const canvasRef = useRef(null);

  // Handle photo upload
  const handlePhotoUpload = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setUserPhoto(event.target.result);
        setProcessedImage(event.target.result);
        saveToHistory();
      };
      reader.readAsDataURL(file);
    }
  };

  // Save state to history for undo/redo
  const saveToHistory = useCallback(() => {
    const state = {
      adjustments: { ...adjustments },
      transforms: { ...transforms },
      filter: selectedFilter,
      template: selectedTemplate
    };
    const newHistory = history.slice(0, historyIndex + 1);
    newHistory.push(state);
    setHistory(newHistory);
    setHistoryIndex(newHistory.length - 1);
  }, [adjustments, transforms, selectedFilter, selectedTemplate, history, historyIndex]);

  // Undo
  const undo = () => {
    if (historyIndex > 0) {
      const prevState = history[historyIndex - 1];
      setAdjustments(prevState.adjustments);
      setTransforms(prevState.transforms);
      setSelectedFilter(prevState.filter);
      setSelectedTemplate(prevState.template);
      setHistoryIndex(historyIndex - 1);
    }
  };

  // Redo
  const redo = () => {
    if (historyIndex < history.length - 1) {
      const nextState = history[historyIndex + 1];
      setAdjustments(nextState.adjustments);
      setTransforms(nextState.transforms);
      setSelectedFilter(nextState.filter);
      setSelectedTemplate(nextState.template);
      setHistoryIndex(historyIndex + 1);
    }
  };

  // Reset all adjustments
  const resetAdjustments = () => {
    setAdjustments({
      brightness: 100,
      contrast: 100,
      saturation: 100,
      warmth: 0,
      exposure: 50,
      highlights: 50,
      shadows: 50,
      sharpness: 0,
      vignette: 0,
      blur: 0,
      fade: 0,
      grain: 0,
      tint: 50,
      vibrance: 50
    });
    setTransforms({
      scale: 1,
      rotation: 0,
      flipH: false,
      flipV: false,
      x: 0,
      y: 0
    });
    setSelectedFilter(FILTERS[0]);
    saveToHistory();
  };

  // Generate CSS filter string
  const getFilterString = () => {
    let filter = selectedFilter.filter;
    if (filter === 'none') {
      filter = '';
    } else {
      filter += ' ';
    }
    
    // Core adjustments
    filter += `brightness(${adjustments.brightness / 100}) `;
    filter += `contrast(${adjustments.contrast / 100}) `;
    filter += `saturate(${adjustments.saturation / 100}) `;
    
    // Warmth/Temperature
    if (adjustments.warmth > 0) {
      filter += `sepia(${adjustments.warmth / 200}) `;
    } else if (adjustments.warmth < 0) {
      filter += `hue-rotate(${adjustments.warmth / 2}deg) `;
    }
    
    // Blur
    if (adjustments.blur > 0) {
      filter += `blur(${adjustments.blur / 10}px) `;
    }
    
    // Exposure (brightness boost)
    if (adjustments.exposure !== 50) {
      filter += `brightness(${1 + (adjustments.exposure - 50) / 100}) `;
    }
    
    // Vibrance (extra saturation)
    if (adjustments.vibrance !== 50) {
      filter += `saturate(${1 + (adjustments.vibrance - 50) / 100}) `;
    }
    
    // Tint (hue rotation)
    if (adjustments.tint !== 50) {
      filter += `hue-rotate(${(adjustments.tint - 50) * 2}deg) `;
    }
    
    // Fade effect (reduced contrast + brightness)
    if (adjustments.fade > 0) {
      filter += `contrast(${1 - adjustments.fade / 200}) `;
    }
    
    // Highlights adjustment (approximate with brightness)
    if (adjustments.highlights !== 50) {
      // This is a simplified approximation
      filter += `brightness(${1 + (adjustments.highlights - 50) / 200}) `;
    }
    
    // Grayscale for desaturation
    if (adjustments.saturation < 100) {
      filter += `grayscale(${(100 - adjustments.saturation) / 200}) `;
    }
    
    return filter.trim();
  };

  // Generate transform string
  const getTransformString = () => {
    let transform = '';
    transform += `scale(${transforms.scale}) `;
    transform += `rotate(${transforms.rotation}deg) `;
    transform += `scaleX(${transforms.flipH ? -1 : 1}) `;
    transform += `scaleY(${transforms.flipV ? -1 : 1}) `;
    transform += `translate(${transforms.x}px, ${transforms.y}px)`;
    return transform;
  };

  // Get aspect ratio dimensions
  const getAspectDimensions = () => {
    switch (aspectRatio) {
      case '1:1': return { width: 400, height: 400 };
      case '4:5': return { width: 400, height: 500 };
      case '9:16': return { width: 360, height: 640 };
      case '16:9': return { width: 640, height: 360 };
      default: return { width: 400, height: 400 };
    }
  };

  // Download image
  const downloadImage = async () => {
    setIsProcessing(true);
    
    try {
      const canvas = document.createElement('canvas');
      const { width, height } = getAspectDimensions();
      const scale = 3; // High resolution export
      canvas.width = width * scale;
      canvas.height = height * scale;
      const ctx = canvas.getContext('2d');
      
      // Draw background
      if (selectedTemplate.background.includes('gradient')) {
        const gradient = ctx.createLinearGradient(0, 0, canvas.width, canvas.height);
        gradient.addColorStop(0, '#fdfbfb');
        gradient.addColorStop(1, '#ebedee');
        ctx.fillStyle = gradient;
      } else {
        ctx.fillStyle = selectedTemplate.background;
      }
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      // Draw image with filters
      if (processedImage) {
        const img = new window.Image();
        img.crossOrigin = 'anonymous';
        img.src = processedImage;
        await new Promise((resolve) => { img.onload = resolve; });
        
        ctx.filter = getFilterString();
        ctx.save();
        ctx.translate(canvas.width / 2, canvas.height / 2);
        ctx.scale(transforms.scale * (transforms.flipH ? -1 : 1), transforms.scale * (transforms.flipV ? -1 : 1));
        ctx.rotate(transforms.rotation * Math.PI / 180);
        
        const imgAspect = img.width / img.height;
        const canvasAspect = canvas.width / canvas.height;
        let drawWidth, drawHeight;
        
        if (imgAspect > canvasAspect) {
          drawHeight = canvas.height;
          drawWidth = drawHeight * imgAspect;
        } else {
          drawWidth = canvas.width;
          drawHeight = drawWidth / imgAspect;
        }
        
        ctx.drawImage(img, -drawWidth / 2, -drawHeight / 2, drawWidth, drawHeight);
        ctx.restore();
      }
      
      // Add vignette effect
      if (adjustments.vignette > 0) {
        const gradient = ctx.createRadialGradient(
          canvas.width / 2, canvas.height / 2, 0,
          canvas.width / 2, canvas.height / 2, Math.max(canvas.width, canvas.height) / 2
        );
        gradient.addColorStop(0.5, 'transparent');
        gradient.addColorStop(1, `rgba(0,0,0,${adjustments.vignette / 100})`);
        ctx.fillStyle = gradient;
        ctx.fillRect(0, 0, canvas.width, canvas.height);
      }
      
      // Download
      const link = document.createElement('a');
      link.download = `content-studio-${Date.now()}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    } catch (error) {
      console.error('Download error:', error);
    }
    
    setIsProcessing(false);
  };

  // Share to Instagram (opens share dialog)
  const shareToInstagram = () => {
    alert('To share to Instagram:\n\n1. Download your image\n2. Open Instagram on your phone\n3. Create a new post and select your downloaded image\n\nInstagram doesn\'t allow direct posting from web apps for security reasons.');
  };

  const dimensions = getAspectDimensions();

  return (
    <div className="min-h-screen py-20">
      {/* Header */}
      <div className="glass-card border-b border-stone-200/80 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-suede-600 to-suede-500 flex items-center justify-center">
                <Camera className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-light">
                  <span className="bg-gradient-to-r from-suede-600 to-terracotta-500 bg-clip-text text-transparent font-semibold">Content Studio</span>
                </h1>
                <p className="text-xs text-stone-500">Create professional social media content</p>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              {/* Undo/Redo */}
              <button
                onClick={undo}
                disabled={historyIndex <= 0}
                className="p-2 rounded-2xl hover:bg-stone-100 disabled:opacity-50 disabled:cursor-not-allowed"
                title="Undo"
              >
                <Undo className="w-5 h-5 text-stone-600" />
              </button>
              <button
                onClick={redo}
                disabled={historyIndex >= history.length - 1}
                className="p-2 rounded-2xl hover:bg-stone-100 disabled:opacity-50 disabled:cursor-not-allowed"
                title="Redo"
              >
                <Redo className="w-5 h-5 text-stone-600" />
              </button>
              
              <div className="w-px h-6 bg-stone-200 mx-2" />
              
              {/* Reset */}
              <button
                onClick={resetAdjustments}
                className="px-3 py-2 text-sm text-stone-600 hover:bg-stone-100 rounded-lg flex items-center gap-1"
              >
                <RotateCcw className="w-4 h-4" />
                Reset
              </button>
              
              {/* Preview */}
              <button
                onClick={() => setShowPreview(true)}
                className="px-3 py-2 text-sm text-stone-600 hover:bg-stone-100 rounded-lg flex items-center gap-1"
              >
                <Eye className="w-4 h-4" />
                Preview
              </button>
              
              {/* Download */}
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={downloadImage}
                disabled={!processedImage || isProcessing}
                className="px-4 py-2 bg-gradient-to-r from-suede-600 to-suede-500 text-white rounded-2xl flex items-center gap-2 disabled:opacity-50"
              >
                <Download className="w-4 h-4" />
                {isProcessing ? 'Processing...' : 'Download'}
              </motion.button>
              
              {/* Share */}
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={shareToInstagram}
                disabled={!processedImage}
                className="px-4 py-2 bg-gradient-to-r from-suede-600 to-terracotta-500 text-white rounded-2xl flex items-center gap-2 disabled:opacity-50"
              >
                <Instagram className="w-4 h-4" />
                Share
              </motion.button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="grid grid-cols-12 gap-6">
          {/* Left Sidebar - Tools */}
          <div className="col-span-3 space-y-4">
            {/* Upload Section */}
            <div className="glass-card rounded-2xl p-4">
              <h3 className="font-semibold text-stone-800 mb-3 flex items-center gap-2">
                <div className="w-6 h-6 rounded-xl bg-gradient-to-br from-suede-500 to-suede-400 flex items-center justify-center">
                  <Upload className="w-3 h-3 text-white" />
                </div>
                Your Photo
              </h3>
              
              {!userPhoto ? (
                <motion.div
                  whileHover={{ scale: 1.02 }}
                  onClick={() => fileInputRef.current?.click()}
                  className="border-2 border-dashed border-suede-200 rounded-2xl p-8 text-center cursor-pointer hover:border-suede-400 hover:bg-suede-50/50 transition-all"
                >
                  <Camera className="w-12 h-12 text-suede-300 mx-auto mb-3" />
                  <p className="text-sm font-medium text-stone-600">Upload your photo</p>
                  <p className="text-xs text-stone-400 mt-1">PNG, JPG up to 10MB</p>
                </motion.div>
              ) : (
                <div className="relative group">
                  <img
                    src={userPhoto}
                    alt="Your photo"
                    className="w-full aspect-square object-cover rounded-2xl"
                  />
                  <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-2xl flex items-center justify-center gap-2">
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      className="px-3 py-2 bg-white rounded-2xl text-sm font-medium"
                    >
                      Change
                    </button>
                    <button
                      onClick={() => { setUserPhoto(null); setProcessedImage(null); }}
                      className="px-3 py-2 bg-red-500 text-white rounded-2xl text-sm font-medium"
                    >
                      Remove
                    </button>
                  </div>
                </div>
              )}
              
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handlePhotoUpload}
                className="hidden"
              />
              
              <p className="text-xs text-stone-400 mt-3 text-center">
                💡 Use a clear, well-lit photo of yourself for best results
              </p>
            </div>

            {/* Aspect Ratio */}
            <div className="glass-card rounded-2xl p-4">
              <h3 className="font-semibold text-stone-800 mb-3 flex items-center gap-2">
                <div className="w-6 h-6 rounded-xl bg-gradient-to-br from-terracotta-500 to-terracotta-400 flex items-center justify-center">
                  <Crop className="w-3 h-3 text-white" />
                </div>
                Aspect Ratio
              </h3>
              <div className="grid grid-cols-4 gap-2">
                {['1:1', '4:5', '9:16', '16:9'].map((ratio) => (
                  <button
                    key={ratio}
                    onClick={() => setAspectRatio(ratio)}
                    className={`p-2 rounded-2xl text-xs font-medium transition-all ${
                      aspectRatio === ratio
                        ? 'bg-suede-500 text-white'
                        : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
                    }`}
                  >
                    {ratio}
                  </button>
                ))}
              </div>
              <div className="flex items-center justify-center gap-4 mt-3 text-xs text-stone-400">
                <span className="flex items-center gap-1"><Square className="w-3 h-3" /> Feed</span>
                <span className="flex items-center gap-1"><Smartphone className="w-3 h-3" /> Stories</span>
              </div>
            </div>

            {/* Tool Tabs */}
            <div className="glass-card rounded-2xl overflow-hidden">
              <div className="flex border-b border-stone-100">
                {[
                  { id: 'templates', label: 'Templates', icon: Grid3X3 },
                  { id: 'filters', label: 'Filters', icon: Palette },
                  { id: 'adjust', label: 'Adjust', icon: Sliders },
                  { id: 'effects', label: 'Effects', icon: Wand2 },
                  { id: 'transform', label: 'Transform', icon: Maximize2 }
                ].map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex-1 py-3 text-xs font-medium flex flex-col items-center gap-1 transition-colors ${
                      activeTab === tab.id
                        ? 'text-suede-600 bg-suede-50'
                        : 'text-stone-500 hover:text-stone-700 hover:bg-stone-50'
                    }`}
                  >
                    <tab.icon className="w-4 h-4" />
                    {tab.label}
                  </button>
                ))}
              </div>

              <div className="p-4 max-h-[500px] overflow-y-auto">
                {/* Templates Tab */}
                {activeTab === 'templates' && (
                  <div className="grid grid-cols-2 gap-2">
                    {TEMPLATES.map((template) => (
                      <motion.button
                        key={template.id}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => { setSelectedTemplate(template); saveToHistory(); }}
                        className={`relative aspect-square rounded-2xl overflow-hidden ${
                          selectedTemplate.id === template.id
                            ? 'ring-2 ring-suede-500 ring-offset-2'
                            : ''
                        }`}
                        style={{ background: template.background }}
                      >
                        <div className="absolute inset-0 flex items-center justify-center text-2xl">
                          {template.preview}
                        </div>
                        <div className="absolute bottom-0 inset-x-0 bg-black/50 text-white text-[10px] py-1 text-center">
                          {template.name}
                        </div>
                      </motion.button>
                    ))}
                  </div>
                )}

                {/* Filters Tab */}
                {activeTab === 'filters' && (
                  <div className="grid grid-cols-3 gap-2">
                    {FILTERS.map((filter) => (
                      <button
                        key={filter.id}
                        onClick={() => { setSelectedFilter(filter); saveToHistory(); }}
                        className={`relative aspect-square rounded-2xl overflow-hidden ${
                          selectedFilter.id === filter.id
                            ? 'ring-2 ring-suede-500 ring-offset-1'
                            : ''
                        }`}
                      >
                        <div
                          className="w-full h-full bg-gradient-to-br from-suede-200 to-terracotta-200"
                          style={{ filter: filter.filter }}
                        />
                        <div className="absolute bottom-0 inset-x-0 bg-black/60 text-white text-[10px] py-1 text-center">
                          {filter.name}
                        </div>
                      </button>
                    ))}
                  </div>
                )}

                {/* Adjust Tab */}
                {activeTab === 'adjust' && (
                  <div className="space-y-4">
                    <p className="text-xs text-stone-500 mb-2">🎚️ Fine-tune your image</p>
                    {[
                      { key: 'brightness', label: 'Brightness', icon: Sun, min: 50, max: 150 },
                      { key: 'contrast', label: 'Contrast', icon: Contrast, min: 50, max: 150 },
                      { key: 'saturation', label: 'Saturation', icon: Droplets, min: 0, max: 200 },
                      { key: 'vibrance', label: 'Vibrance', icon: Star, min: 0, max: 100 },
                      { key: 'warmth', label: 'Warmth', icon: Thermometer, min: -50, max: 50 },
                      { key: 'tint', label: 'Tint', icon: Palette, min: 0, max: 100 },
                      { key: 'exposure', label: 'Exposure', icon: Sun, min: 0, max: 100 },
                      { key: 'highlights', label: 'Highlights', icon: Sun, min: 0, max: 100 },
                      { key: 'shadows', label: 'Shadows', icon: Circle, min: 0, max: 100 }
                    ].map((control) => (
                      <div key={control.key}>
                        <div className="flex items-center justify-between mb-1">
                          <label className="text-xs font-medium text-stone-600 flex items-center gap-1">
                            <control.icon className="w-3 h-3" />
                            {control.label}
                          </label>
                          <span className="text-xs text-stone-400">{adjustments[control.key]}</span>
                        </div>
                        <input
                          type="range"
                          min={control.min}
                          max={control.max}
                          value={adjustments[control.key]}
                          onChange={(e) => {
                            setAdjustments({ ...adjustments, [control.key]: parseInt(e.target.value) });
                          }}
                          onMouseUp={saveToHistory}
                          className="w-full h-2 bg-stone-200 rounded-2xl appearance-none cursor-pointer accent-suede-500"
                        />
                      </div>
                    ))}
                  </div>
                )}

                {/* Effects Tab - NEW */}
                {activeTab === 'effects' && (
                  <div className="space-y-4">
                    <p className="text-xs text-stone-500 mb-2">✨ Add special effects</p>
                    {[
                      { key: 'vignette', label: 'Vignette', icon: Circle, min: 0, max: 50, desc: 'Dark edges' },
                      { key: 'blur', label: 'Blur', icon: Focus, min: 0, max: 30, desc: 'Soft focus' },
                      { key: 'fade', label: 'Fade', icon: Layers, min: 0, max: 50, desc: 'Washed out' },
                      { key: 'grain', label: 'Film Grain', icon: Star, min: 0, max: 50, desc: 'Vintage texture' },
                      { key: 'sharpness', label: 'Sharpen', icon: Focus, min: 0, max: 100, desc: 'Edge clarity' }
                    ].map((control) => (
                      <div key={control.key}>
                        <div className="flex items-center justify-between mb-1">
                          <label className="text-xs font-medium text-stone-600 flex items-center gap-1">
                            <control.icon className="w-3 h-3" />
                            {control.label}
                          </label>
                          <span className="text-xs text-stone-400">{adjustments[control.key]}</span>
                        </div>
                        <input
                          type="range"
                          min={control.min}
                          max={control.max}
                          value={adjustments[control.key]}
                          onChange={(e) => {
                            setAdjustments({ ...adjustments, [control.key]: parseInt(e.target.value) });
                          }}
                          onMouseUp={saveToHistory}
                          className="w-full h-2 bg-stone-200 rounded-2xl appearance-none cursor-pointer accent-suede-500"
                        />
                        <p className="text-[10px] text-stone-400 mt-0.5">{control.desc}</p>
                      </div>
                    ))}
                    
                    {/* Quick Effect Presets */}
                    <div className="pt-4 border-t border-stone-100">
                      <h4 className="text-xs font-semibold text-stone-700 mb-3">Quick Presets</h4>
                      <div className="grid grid-cols-2 gap-2">
                        {[
                          { name: 'Dreamy', settings: { blur: 5, fade: 20, vignette: 15 } },
                          { name: 'Film', settings: { grain: 30, fade: 10, vignette: 20 } },
                          { name: 'Crisp', settings: { sharpness: 50, blur: 0, fade: 0 } },
                          { name: 'Moody', settings: { vignette: 35, fade: 15, grain: 10 } },
                          { name: 'Soft', settings: { blur: 10, fade: 15, sharpness: 0 } },
                          { name: 'Retro', settings: { grain: 40, fade: 25, vignette: 25 } }
                        ].map((preset) => (
                          <button
                            key={preset.name}
                            onClick={() => {
                              setAdjustments({ ...adjustments, ...preset.settings });
                              saveToHistory();
                            }}
                            className="py-2 px-3 bg-stone-100 hover:bg-stone-200 rounded-2xl text-xs font-medium text-stone-600 hover:text-suede-600 transition-colors"
                          >
                            {preset.name}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* Transform Tab - NEW */}
                {activeTab === 'transform' && (
                  <div className="space-y-4">
                    <p className="text-xs text-stone-500 mb-2">🔄 Resize & rotate</p>
                    
                    {/* Rotation */}
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <label className="text-xs font-medium text-stone-600">Rotation</label>
                        <span className="text-xs text-stone-400">{transforms.rotation}°</span>
                      </div>
                      <input
                        type="range"
                        min={-180}
                        max={180}
                        value={transforms.rotation}
                        onChange={(e) => setTransforms({ ...transforms, rotation: parseInt(e.target.value) })}
                        onMouseUp={saveToHistory}
                        className="w-full h-2 bg-stone-200 rounded-2xl appearance-none cursor-pointer accent-terracotta-500"
                      />
                      {/* Quick rotation buttons */}
                      <div className="flex gap-1 mt-2">
                        {[-90, -45, 0, 45, 90].map((deg) => (
                          <button
                            key={deg}
                            onClick={() => { setTransforms({ ...transforms, rotation: deg }); saveToHistory(); }}
                            className={`flex-1 py-1 rounded text-[10px] font-medium ${
                              transforms.rotation === deg ? 'bg-terracotta-500 text-white' : 'bg-stone-100 text-stone-600'
                            }`}
                          >
                            {deg}°
                          </button>
                        ))}
                      </div>
                    </div>
                    
                    {/* Scale */}
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <label className="text-xs font-medium text-stone-600">Scale</label>
                        <span className="text-xs text-stone-400">{(transforms.scale * 100).toFixed(0)}%</span>
                      </div>
                      <input
                        type="range"
                        min={50}
                        max={200}
                        value={transforms.scale * 100}
                        onChange={(e) => setTransforms({ ...transforms, scale: parseInt(e.target.value) / 100 })}
                        onMouseUp={saveToHistory}
                        className="w-full h-2 bg-stone-200 rounded-2xl appearance-none cursor-pointer accent-terracotta-500"
                      />
                    </div>
                    
                    {/* Position */}
                    <div>
                      <label className="text-xs font-medium text-stone-600 mb-2 block">Position</label>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <span className="text-[10px] text-stone-400">X Offset</span>
                          <input
                            type="range"
                            min={-100}
                            max={100}
                            value={transforms.x}
                            onChange={(e) => setTransforms({ ...transforms, x: parseInt(e.target.value) })}
                            className="w-full h-2 bg-stone-200 rounded-2xl appearance-none cursor-pointer accent-suede-500"
                          />
                        </div>
                        <div>
                          <span className="text-[10px] text-stone-400">Y Offset</span>
                          <input
                            type="range"
                            min={-100}
                            max={100}
                            value={transforms.y}
                            onChange={(e) => setTransforms({ ...transforms, y: parseInt(e.target.value) })}
                            className="w-full h-2 bg-stone-200 rounded-2xl appearance-none cursor-pointer accent-suede-500"
                          />
                        </div>
                      </div>
                    </div>
                    
                    {/* Flip buttons */}
                    <div className="pt-3 border-t border-stone-100">
                      <label className="text-xs font-medium text-stone-600 mb-2 block">Flip</label>
                      <div className="flex gap-2">
                        <button
                          onClick={() => { setTransforms({ ...transforms, flipH: !transforms.flipH }); saveToHistory(); }}
                          className={`flex-1 py-2.5 rounded-2xl text-xs font-medium flex items-center justify-center gap-1 transition-colors ${
                            transforms.flipH ? 'bg-terracotta-500 text-white' : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
                          }`}
                        >
                          <FlipHorizontal className="w-4 h-4" />
                          Horizontal
                        </button>
                        <button
                          onClick={() => { setTransforms({ ...transforms, flipV: !transforms.flipV }); saveToHistory(); }}
                          className={`flex-1 py-2.5 rounded-2xl text-xs font-medium flex items-center justify-center gap-1 transition-colors ${
                            transforms.flipV ? 'bg-terracotta-500 text-white' : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
                          }`}
                        >
                          <FlipVertical className="w-4 h-4" />
                          Vertical
                        </button>
                      </div>
                    </div>
                    
                    {/* Reset Transform */}
                    <button
                      onClick={() => {
                        setTransforms({ scale: 1, rotation: 0, flipH: false, flipV: false, x: 0, y: 0 });
                        saveToHistory();
                      }}
                      className="w-full py-2 bg-stone-100 hover:bg-stone-200 rounded-2xl text-xs font-medium text-stone-600 flex items-center justify-center gap-1 mt-2"
                    >
                      <RotateCcw className="w-3 h-3" />
                      Reset Transform
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Center - Canvas Preview */}
          <div className="col-span-6 flex flex-col items-center">
            <div className="glass-card rounded-2xl p-6 w-full">
              <div className="flex items-center justify-center">
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="relative rounded-2xl overflow-hidden shadow-xl"
                  style={{
                    width: dimensions.width,
                    height: dimensions.height,
                    background: selectedTemplate.background
                  }}
                >
                  {processedImage ? (
                    <div
                      className="w-full h-full flex items-center justify-center overflow-hidden"
                      style={{ ...selectedTemplate.style }}
                    >
                      <img
                        src={processedImage}
                        alt="Your content"
                        className="w-full h-full object-cover"
                        style={{
                          filter: getFilterString(),
                          transform: getTransformString()
                        }}
                      />
                      {/* Vignette overlay */}
                      {adjustments.vignette > 0 && (
                        <div
                          className="absolute inset-0 pointer-events-none"
                          style={{
                            background: `radial-gradient(circle, transparent 40%, rgba(0,0,0,${adjustments.vignette / 100}) 100%)`
                          }}
                        />
                      )}
                    </div>
                  ) : (
                    <div className="w-full h-full flex flex-col items-center justify-center text-stone-400">
                      <User className="w-16 h-16 mb-4 opacity-30" />
                      <p className="text-sm">Upload a photo to get started</p>
                    </div>
                  )}
                </motion.div>
              </div>
              
              {/* Quick zoom controls */}
              <div className="flex items-center justify-center gap-2 mt-4">
                <button
                  onClick={() => setTransforms({ ...transforms, scale: Math.max(0.5, transforms.scale - 0.1) })}
                  className="p-2 rounded-2xl hover:bg-stone-100"
                >
                  <ZoomOut className="w-4 h-4 text-stone-500" />
                </button>
                <span className="text-xs text-stone-400 w-12 text-center">{(transforms.scale * 100).toFixed(0)}%</span>
                <button
                  onClick={() => setTransforms({ ...transforms, scale: Math.min(2, transforms.scale + 0.1) })}
                  className="p-2 rounded-2xl hover:bg-stone-100"
                >
                  <ZoomIn className="w-4 h-4 text-stone-500" />
                </button>
              </div>
            </div>
          </div>

          {/* Right Sidebar - Preview & Caption */}
          <div className="col-span-3 space-y-4">
            {/* Instagram Preview */}
            <div className="glass-card rounded-2xl overflow-hidden">
              <div className="p-3 border-b border-stone-200/80 flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-suede-500 to-terracotta-500" />
                <div>
                  <p className="text-sm font-semibold text-stone-800">your_username</p>
                  <p className="text-xs text-stone-400">Your Location</p>
                </div>
                <MoreHorizontal className="w-5 h-5 text-stone-400 ml-auto" />
              </div>
              
              <div
                className="aspect-square bg-stone-100 flex items-center justify-center overflow-hidden"
                style={{ background: selectedTemplate.background }}
              >
                {processedImage ? (
                  <img
                    src={processedImage}
                    alt="Preview"
                    className="w-full h-full object-cover"
                    style={{
                      filter: getFilterString(),
                      transform: getTransformString()
                    }}
                  />
                ) : (
                  <User className="w-12 h-12 text-stone-300" />
                )}
              </div>
              
              <div className="p-3">
                <div className="flex items-center gap-4">
                  <Heart className="w-6 h-6 text-stone-700" />
                  <MessageCircle className="w-6 h-6 text-stone-700" />
                  <Send className="w-6 h-6 text-stone-700" />
                  <Bookmark className="w-6 h-6 text-stone-700 ml-auto" />
                </div>
                <p className="text-sm font-semibold text-stone-800 mt-2">1,234 likes</p>
              </div>
            </div>

            {/* Caption */}
            <div className="glass-card rounded-2xl p-4">
              <h3 className="font-semibold text-stone-800 mb-3 flex items-center gap-2">
                <div className="w-6 h-6 rounded-xl bg-gradient-to-br from-suede-500 to-suede-400 flex items-center justify-center">
                  <Type className="w-3 h-3 text-white" />
                </div>
                Caption
              </h3>
              <textarea
                value={caption}
                onChange={(e) => setCaption(e.target.value)}
                placeholder="Write a caption... ✨"
                className="w-full h-24 px-3 py-2 text-sm border border-stone-200/80 rounded-2xl resize-none focus:outline-none focus:ring-2 focus:ring-suede-500/20"
              />
              <div className="flex items-center justify-between mt-2">
                <span className="text-xs text-stone-400">{caption.length}/2,200</span>
                <button
                  onClick={() => navigator.clipboard.writeText(caption)}
                  className="text-xs text-suede-500 hover:text-suede-600"
                >
                  Copy
                </button>
              </div>
              
              {/* Hashtag suggestions */}
              <div className="mt-3 flex flex-wrap gap-1">
                {['#ootd', '#fashion', '#style', '#look', '#aesthetic'].map((tag) => (
                  <button
                    key={tag}
                    onClick={() => setCaption(caption + ' ' + tag)}
                    className="px-2 py-1 bg-stone-100 rounded-full text-xs text-stone-600 hover:bg-suede-100 hover:text-suede-600"
                  >
                    {tag}
                  </button>
                ))}
              </div>
            </div>

            {/* Tips */}
            <div className="glass-card rounded-2xl p-4">
              <h3 className="font-semibold text-stone-800 mb-2 flex items-center gap-2">
                <div className="w-6 h-6 rounded-xl bg-gradient-to-br from-terracotta-500 to-terracotta-400 flex items-center justify-center">
                  <Sparkles className="w-3 h-3 text-white" />
                </div>
                Pro Tips
              </h3>
              <ul className="text-xs text-stone-600 space-y-2">
                <li className="flex items-start gap-2">
                  <span className="text-suede-500">✓</span>
                  Use natural lighting for the most realistic look
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-suede-500">✓</span>
                  Keep adjustments subtle for authentic results
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-suede-500">✓</span>
                  Match your filter to your feed's aesthetic
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-suede-500">✓</span>
                  Post during peak engagement hours (11am-1pm, 7-9pm)
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Preview Modal */}
      <AnimatePresence>
        {showPreview && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
            onClick={() => setShowPreview(false)}
          >
            <motion.div
              initial={{ scale: 0.8 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.8 }}
              className="relative max-w-full max-h-full"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => setShowPreview(false)}
                className="absolute -top-8 md:-top-10 right-0 text-white/70 hover:text-white"
              >
                <X className="w-6 h-6 md:w-8 md:h-8" />
              </button>
              
              <div
                className="rounded-xl md:rounded-2xl overflow-hidden shadow-2xl max-w-[90vw] max-h-[85vh]"
                style={{
                  width: 'min(90vw, ' + (dimensions.width * 1.5) + 'px)',
                  height: 'min(85vh, ' + (dimensions.height * 1.5) + 'px)',
                  background: selectedTemplate.background
                }}
              >
                {processedImage && (
                  <img
                    src={processedImage}
                    alt="Full preview"
                    className="w-full h-full object-cover"
                    style={{
                      filter: getFilterString(),
                      transform: getTransformString()
                    }}
                  />
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ContentStudio;
